import { appURLs } from "@/app/constants.mjs";
import { DEFAULT_LOCALE, regionToLanguage } from "@/game-tft/constants.mjs";
import * as GraphQLTemplates from "@/game-tft/graphql-templates.mjs";
import parseSearchParams from "@/util/parse-search-params.mjs";

// This module is solely responsible for constructing API requests and nothing else.

export function getPlayer(platform, gameName, tagLine) {
  return `${appURLs.TFT}/riot_account/tft/${platform}/${gameName}/${tagLine}`;
}

export function getProMatchIds() {
  return `${appURLs.TFT}/tft/pro_match_ids`;
}

export function getLeagueProfile(region, summonerName) {
  const query = GraphQLTemplates.LeagueProfile;
  const variables = {
    region: region.toUpperCase(),
    summoner_name: summonerName,
  };

  const params = parseSearchParams({ query, variables });

  const url = `${appURLs.RIOT}/graphql?${params.toString()}`;
  return url;
}

export function getMatchList(platform, gameName, tagLine) {
  return `${appURLs.TFT}/tft/match_list/${platform}/${gameName}/${tagLine}`;
}

export function getMatch(matchId) {
  return `${appURLs.TFT}/tft/match/${matchId}`;
}

export function getMatchRoundData(matchId) {
  return `${appURLs.TFT}/tft/round_data/${matchId}`;
}

export function getMatchListFixture() {
  return `${appURLs.CDN_WEB}/reindex/tft_matchlist.json`;
}

export function getPostmatchCoachingScores({ queryName, patch, mode }) {
  const query = GraphQLTemplates.ExecuteDatabricksQuery;
  const variables = {
    game: "TFT",
    queryName: queryName,
    params: [
      { name: "patch", value: patch },
      { name: "mode", value: mode },
    ],
  };

  const params = parseSearchParams({ query, variables });

  const url = `${appURLs.DATALAKE}/graphql?${params.toString()}`;
  return url;
}

export function getUnitPowerupTierStats({ mode = "RANKED" }) {
  const query = GraphQLTemplates.ExecuteDatabricksQuery;
  const variables = {
    game: "TFT",
    queryName: "unit_bonus_type_round_stats",
    params: [
      { name: "mode", value: mode },
      { name: "unit_bonus_type", value: "TFT15_powerup" },
    ],
  };

  const params = parseSearchParams({ query, variables });

  const url = `${appURLs.DATALAKE}/graphql?${params.toString()}`;
  return url;
}

export function getUnitPowerupStats({ mode = "RANKED" }) {
  const query = GraphQLTemplates.ExecuteDatabricksQuery;
  const variables = {
    game: "TFT",
    queryName: "unit_bonus_round_stats",
    params: [
      { name: "mode", value: mode },
      { name: "unit_bonus_type", value: "TFT15_powerup" },
    ],
  };

  const params = parseSearchParams({ query, variables });

  const url = `${appURLs.DATALAKE}/graphql?${params.toString()}`;
  return url;
}

// export function getPostMatchExtra({ region, name, matchId }) {
//   return `${appURLs.TFT_DEPRECATED_AGGREGATE}/getPostmatch?${parseSearchParams({
//     region,
//     name,
//     matchId,
//   })}`;
// }

export function getPostMatchExtraFixture() {
  return `${appURLs.CDN_WEB}/reindex/tft_postmatch_extra.json`;
}

export function getChampions() {
  return `${appURLs.UTILS_STATIC}/tft/champions`;
}

export function getClasses() {
  return `${appURLs.UTILS_STATIC}/tft/classes`;
}

export function getOrigins() {
  return `${appURLs.UTILS_STATIC}/tft/origins`;
}

export function getItems() {
  return `${appURLs.UTILS_STATIC}/tft/items`;
}

export function getAugments() {
  return `${appURLs.UTILS_STATIC}/tft/augments`;
}

export function getPortals() {
  return `${appURLs.UTILS_STATIC}/tft/portals`;
}

export function getStatsData({ patch, page, filters }) {
  return `${appURLs.UTILS_STATIC}/tft/stats/data/${patch}/${page}_stats:${filters}`;
}

export function getCompsData({ patch, filters, type, isTempMode }) {
  return `${appURLs.UTILS_STATIC}/tft/stats/data/${patch}/${
    isTempMode ? "temp_mode_" : ""
  }${type}_comps:${filters}`;
}

export function getCompsDetailData({ patch, filters, tab, type, isTempMode }) {
  return `${appURLs.UTILS_STATIC}/tft/stats/data/${patch}/${
    isTempMode ? "temp_mode_" : ""
  }${type}_comps_${tab}_stats:${filters}`;
}

export function getLevelingMethods() {
  return `${appURLs.UTILS_STATIC}/tft/levelingMethods`;
}

export function getOverlayConfiguration() {
  return `${appURLs.UTILS_STATIC}/tft/overlay/configuration`;
}

export function getIncomeStats(tier, patch) {
  return `${appURLs.UTILS_STATIC}/tft/stats/data/${patch}/income_stats:patch=${patch}&rank=${tier}`;
}

export function getLatestPatch(set) {
  if (!set)
    return `${appURLs.UTILS_STATIC}/tft/stats/data/patches/latest/latest.json`;
  return `${appURLs.UTILS_STATIC}/tft/stats/data/patches/${set
    .replace("set", "")
    .replace("_", ".")}/latest.json`;
}

export function getLatestPBEPatch() {
  return `${appURLs.UTILS_STATIC}/tft/stats/data/patches/latest/pbe.json`;
}

export function getDefaultFilters(patch) {
  return `${appURLs.UTILS_STATIC}/tft/stats/data/${patch}/analyzed_comps_default_mode_rank_filters`;
}

// new automatic static data format
const DATA_BASE_URL = `${appURLs.UTILS_STATIC}/tftTest`;
export function getChampionsV2(set) {
  return `${DATA_BASE_URL}/${set}/units`;
}

export function getTraitsV2(set) {
  return `${DATA_BASE_URL}/${set}/traits`;
}

export function getItemsV2(set) {
  return `${DATA_BASE_URL}/${set}/items`;
}

export function getPortalsV2(set) {
  return `${DATA_BASE_URL}/${set}/portals`;
}

export function getAugmentsV2(set) {
  return `${DATA_BASE_URL}/${set}/augments`;
}

export function getEncountersV2(set) {
  if (set === "set11" || set === "set13")
    return `${DATA_BASE_URL}/${set}/encounters`;
}

export function getCharmsV2(set) {
  if (set === "set12") return `${DATA_BASE_URL}/${set}/charms`;
}

export function getPowerups(set) {
  if (set === "set15") return `${DATA_BASE_URL}/${set}/powerups`;
}

export function getUnlockables(set) {
  if (set === "set16") return `${DATA_BASE_URL}/${set}/unlockables`;
}

export function getLootTables(set) {
  return `${DATA_BASE_URL}/${set}/loot-tables`;
}

export function getUnitsLocale(locale, set) {
  const ritoLocale = (
    regionToLanguage[locale] ?? DEFAULT_LOCALE
  )?.toLowerCase();
  return `${DATA_BASE_URL}/${set}/${ritoLocale}/units`;
}

export function getTraitsLocale(locale, set) {
  const ritoLocale = (
    regionToLanguage[locale] ?? DEFAULT_LOCALE
  )?.toLowerCase();
  return `${DATA_BASE_URL}/${set}/${ritoLocale}/traits`;
}

export function getItemsLocale(locale, set) {
  const ritoLocale = (
    regionToLanguage[locale] ?? DEFAULT_LOCALE
  )?.toLowerCase();
  return `${DATA_BASE_URL}/${set}/${ritoLocale}/items`;
}

export function getPortalsLocale(locale, set) {
  const ritoLocale = (
    regionToLanguage[locale] ?? DEFAULT_LOCALE
  )?.toLowerCase();
  return `${DATA_BASE_URL}/${set}/${ritoLocale}/portals`;
}

export function getAugmentsLocale(locale, set) {
  const ritoLocale = (
    regionToLanguage[locale] ?? DEFAULT_LOCALE
  )?.toLowerCase();
  return `${DATA_BASE_URL}/${set}/${ritoLocale}/augments`;
}

export function getCharmsLocale(locale, set) {
  const ritoLocale = (
    regionToLanguage[locale] ?? DEFAULT_LOCALE
  )?.toLowerCase();
  return `${DATA_BASE_URL}/${set}/${ritoLocale}/charms`;
}

export function getPowerupsLocale(locale, set) {
  const ritoLocale = (
    regionToLanguage[locale] ?? DEFAULT_LOCALE
  )?.toLowerCase();
  return `${appURLs.UTILS_STATIC}/tftTest/${set}/${ritoLocale}/powerups`;
}

export function getUnlockablesLocale(locale, set) {
  const ritoLocale = (
    regionToLanguage[locale] ?? DEFAULT_LOCALE
  )?.toLowerCase();
  return `${DATA_BASE_URL}/${set}/${ritoLocale}/unlockables`;
}
